import React, { FC } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import EventImages from 'components/events/EventImages';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { useEvent } from 'components/context/EventContext';
import { AllTimelineGqlOptions, useTimeline } from 'components/events/Timeline/TimelineContext';

import { EventImageProps } from './interfaces';
import { EventType, Modals } from 'types/enums';
import { PickFilesModalProps } from 'components/modals/PickFilesModal';

import useStyles from './style';

const EventImage: FC<EventImageProps> = ({ placeholder }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { event, isEventDj, isEventGuest = true, isHistory = false } = useEvent();
  const timeline = useTimeline<AllTimelineGqlOptions>();
  const { openModal } = useModal();

  const canEditEventPhoto =
    !isHistory &&
    !isEventGuest &&
    (isEventDj || (!!event?.settings?.canHostChangeEventCoverPhoto && !event?.isLocked));

  const [updateEvent, { loading }] = useMutation(timeline.event.mutations.update.mutation, {
    onError,
  });

  const handleUpdateEventImage = (image: UpdateEventImageInput) =>
    updateEvent({
      variables: {
        ...timeline.variables,
        payload: {
          image,
        },
      },
    });

  return !!event ? (
    <EventImages
      onClick={
        canEditEventPhoto
          ? () => {
              openModal<PickFilesModalProps>({
                key: Modals.pickImage,
                props: {
                  fileSrc: event?.image?.squareUrl,
                  title: t('changePhoto'),
                  onChange: async (_, square) => {
                    handleUpdateEventImage({
                      ...(!!square ? { square } : { squareUrl: '' }),
                    });
                  },
                },
              });
            }
          : undefined
      }
      id={event._id}
      title={event.title}
      type={event.type as EventType}
      squareUrl={event?.image?.cropped?.medium || event?.image?.squareUrl}
      isUpdating={loading}
      className={classes.eventImage}
    >
      <div className={classes.noImageText}>{placeholder}</div>
    </EventImages>
  ) : null;
};

export default EventImage;
